import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faWindow,
  faWindowMaximize,
  faWindowMinimize,
} from "@awesome.me/kit-70c1f0055f/icons/classic/light";
import type { OsContextWindow, RichContext } from "@/hooks/useRichAniContext";
import type { OsAniContextSnapshot } from "@/components/ui/os-layout/buildOsAniContextSnapshot";
import { AgentPayloadJsonView } from "./AgentPayloadJsonView";
import { buildAgentPayloadPreview } from "./agentPayloadPreview";

export function AgentPayloadContextSection({
  richContext,
  osContext,
  currentApp,
  personalityMode,
  companyId,
}: {
  richContext: RichContext;
  osContext?: OsAniContextSnapshot;
  currentApp?: string;
  personalityMode?: string;
  companyId?: string;
}) {
  const preview = buildAgentPayloadPreview({
    richContext,
    osContext,
    currentApp,
    personalityMode,
    companyId,
  });

  const hasPayload =
    Object.keys((preview.app_state as Record<string, unknown>) ?? {}).length > 0 ||
    Object.keys((preview.agent_context as Record<string, unknown>) ?? {}).length > 0 ||
    preview.os_context != null;

  if (!hasPayload) {
    return (
      <p className="text-xs text-slate-500">
        No agent context on the next send yet. Open a focused app window or load home/review standup data.
      </p>
    );
  }

  return (
    <div className="space-y-2" data-testid="developer-menu-agent-payload">
      <p className="text-[10px] text-slate-500">
        Trimmed preview of <span className="font-mono">fe_context</span> (matches worker prompt shaping).
      </p>
      <AgentPayloadJsonView value={preview} />
    </div>
  );
}
type OsWorkspace = NonNullable<RichContext["osContext"]>["workspaces"][number];

export function OsContextSection({ richContext }: { richContext: RichContext }) {
  const osContext = richContext.osContext;
  if (!osContext) {
    return null;
  }

  return (
    <div className="space-y-3">
      {osContext.workspaces.map((ws: OsWorkspace) => (
        <div key={ws.id} className={ws.isActive ? "" : "opacity-60"}>
          <div
            className={`mb-1 text-[10px] uppercase ${ws.isActive ? "text-blue-400" : "text-gray-500"}`}
          >
            {ws.name} {ws.isActive ? "(active)" : ""}
          </div>
          <ul className="space-y-0.5 border-l border-slate-700 pl-2">
            {ws.windows && ws.windows.length > 0 ? (
              ws.windows.map((win: OsContextWindow, idx: number) => (
                <li
                  key={idx}
                  className={`flex items-center gap-2 text-xs ${
                    win.isFocused ? "text-blue-400" : "text-gray-400"
                  }`}
                >
                  <FontAwesomeIcon
                    icon={win.isMinimized ? faWindowMinimize : win.isFocused ? faWindowMaximize : faWindow}
                    className="h-3 w-3 shrink-0"
                  />
                  <span className="font-medium capitalize">{win.appId}</span>
                  <span className="truncate text-[10px] text-gray-500">{win.title}</span>
                  {win.isFocused ? (
                    <span className="shrink-0 text-[10px] text-blue-400/90">· focused</span>
                  ) : null}
                </li>
              ))
            ) : (
              <li className="text-[10px] italic text-gray-600">No windows</li>
            )}
          </ul>
        </div>
      ))}
    </div>
  );
}

export function UrlInferredSection({ richContext }: { richContext: RichContext }) {
  const inferred = richContext.urlInferredContext;
  if (
    !inferred ||
    richContext.osContext ||
    (richContext.apiContext && richContext.apiContext.activeResources.length > 0)
  ) {
    return null;
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center space-x-2">
        <div className="h-1.5 w-1.5 rounded-full bg-amber-500" />
        <span className="flex-none font-medium capitalize text-gray-700 dark:text-gray-300">
          {inferred.resourceType}
        </span>
        <div className="flex-grow truncate">
          {inferred.resourceId ? (
            <span className="truncate font-mono text-[10px] text-gray-500 dark:text-gray-400">
              {inferred.resourceId}
            </span>
          ) : null}
          {inferred.resourceName ? (
            <span className="truncate text-[10px] text-gray-600 dark:text-gray-400">
              {inferred.resourceName}
            </span>
          ) : null}
        </div>
      </div>
      <p className="text-[10px] italic text-amber-600 dark:text-amber-400">
        No API data loaded yet. Context inferred from URL only.
      </p>
    </div>
  );
}
